import { supabase, q } from '../lib/supabase.js';
import * as Demo from '../demo/demo.js';

/**
 * Substitui o array EMPRESAS + lsGet('tarter_empresas_extra') do protótipo.
 */

export const SITUACOES = {
  ativo:         { rotulo: 'Ativo', tag: 'green' },
  sem_movimento: { rotulo: 'Sem movimento', tag: 'amber' },
  encerrado:     { rotulo: 'Ex-cliente', tag: 'red' },
};

const CAMPOS = `
  id, codigo, nome, cnpj, regime, periodicidade,
  situacao, cliente_desde, cliente_ate, motivo_saida, matriz_id,
  zappy, email,
  funcionarios, folha_conosco, pro_labore_somente,
  open_finance, layout, qtd_contas, aplicacoes, contas_no_open, contas_fora_open,
  situacao_doc, ultima_cobranca,
  andamento, status_atual, particularidades,
  ativo, gera_fechamento, validado, sem_conta_bancaria
`;

/**
 * Lista as empresas já com situação calculada (pendências, realizado até,
 * competência aberta) e com os responsáveis. No protótipo isso era
 * `e.pendencias` e `e.realizadoAte` digitados à mão.
 *
 * Atenção ao `matriz:empresas!matriz_id`: quando a tabela aponta para ela
 * mesma, a API exige a dica pela COLUNA. Com o nome da restrição
 * (`empresas_matriz_id_fkey`) ela responde 400 dizendo que não achou a
 * relação — mesmo com a chave existindo e com esse nome exato. Como esta
 * consulta entra num Promise.all, o 400 derrubava a carga inteira e TODAS
 * as telas ficavam vazias.
 */
export async function listar({ incluirEncerrados = false } = {}) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const todas = Demo.empresasComSituacao();
    return incluirEncerrados ? todas : todas.filter((e) => e.situacao !== 'encerrado');
  }

  let sel = supabase
    .from('empresas')
    .select(`
      ${CAMPOS},
      responsaveis:empresa_responsaveis ( colaborador:colaboradores ( id, nome ) ),
      bancos:empresa_bancos ( id, nome, instituicao, agencia, conta, open_finance, ativo ),
      matriz:empresas!matriz_id ( id, nome )
    `)
    .order('nome');

  if (!incluirEncerrados) sel = sel.neq('situacao', 'encerrado');

  const [empresas, situacoes] = await Promise.all([
    q(sel),
    q(supabase.from('v_empresa_situacao').select('*')),
  ]);

  const porId = new Map(situacoes.map((s) => [s.empresa_id, s]));

  return empresas.map((e) => ({
    ...e,
    responsaveis: (e.responsaveis || []).map((r) => r.colaborador).filter(Boolean),
    bancos: (e.bancos || []).filter((b) => b.ativo),
    pendencias: porId.get(e.id)?.pendencias ?? 0,
    realizado_ate: porId.get(e.id)?.realizado_ate ?? null,
    competencia_aberta: porId.get(e.id)?.competencia_aberta ?? null,
  }));
}

export async function buscar(id) {
  const linhas = await listar({ incluirEncerrados: true });
  return linhas.find((e) => e.id === id) || null;
}

export async function criar(dados) {
  const { bancos = [], responsaveis = [], ...empresa } = dados;

  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const nova = {
      id: 'e-demo-' + Date.now(), ativo: true, periodicidade: 'mensal',
      funcionarios: false, folha_conosco: false, pro_labore_somente: false,
      open_finance: false, aplicacoes: false,
      ...empresa,
      responsaveis: responsaveis.map((id) => Demo.colaboradores.find((c) => c.id === id)).filter(Boolean).map((c) => ({ id: c.id, nome: c.nome })),
      bancos: bancos.map((b, i) => ({ id: 'b-demo-' + Date.now() + i, nome: typeof b === 'string' ? b : b.nome, conta: null, open_finance: false, ativo: true })),
    };
    Demo.empresas.push(nova);
    return nova;
  }

  const [nova] = await q(
    supabase.from('empresas').insert(empresa).select(CAMPOS)
  );

  if (bancos.length) {
    await q(
      supabase.from('empresa_bancos').insert(
        bancos.map((b) => ({
          empresa_id: nova.id,
          nome: typeof b === 'string' ? b : b.nome,
          conta: typeof b === 'string' ? null : b.conta ?? null,
          open_finance: typeof b === 'string' ? false : !!b.open_finance,
        }))
      )
    );
  }

  if (responsaveis.length) {
    await definirResponsaveis(nova.id, responsaveis);
  }

  return nova;
}

export async function atualizar(id, campos) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const e = Demo.empresas.find((x) => x.id === id);
    if (e) Object.assign(e, campos);
    return e;
  }

  const [linha] = await q(
    supabase.from('empresas').update(campos).eq('id', id).select(CAMPOS)
  );
  return linha;
}

/** Só a gestão consegue — a RLS barra o colaborador. */
export async function definirResponsaveis(empresaId, colaboradorIds) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const e = Demo.empresas.find((x) => x.id === empresaId);
    if (e) {
      e.responsaveis = colaboradorIds
        .map((id) => Demo.colaboradores.find((c) => c.id === id))
        .filter(Boolean)
        .map((c) => ({ id: c.id, nome: c.nome }));
    }
    return;
  }

  await q(supabase.from('empresa_responsaveis').delete().eq('empresa_id', empresaId));
  if (!colaboradorIds.length) return;
  await q(
    supabase.from('empresa_responsaveis').insert(
      colaboradorIds.map((colaborador_id) => ({ empresa_id: empresaId, colaborador_id }))
    )
  );
}

const CAMPOS_BANCO = 'id, nome, instituicao, banco_codigo, agencia, conta, open_finance, ativo';

export async function adicionarBanco(empresaId, { nome, conta = null, open_finance = false }) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const novo = { id: 'b-demo-' + Date.now(), nome, conta, open_finance, ativo: true };
    const emp = Demo.empresas.find((e) => e.id === empresaId);
    if (emp) (emp.bancos = emp.bancos || []).push(novo);
    return novo;
  }

  const [linha] = await q(
    supabase
      .from('empresa_bancos')
      .insert({ empresa_id: empresaId, nome, conta, open_finance })
      .select(CAMPOS_BANCO)
  );
  return linha;
}

export async function renomearBanco(bancoId, nome) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    for (const e of Demo.empresas) {
      const b = (e.bancos || []).find((x) => x.id === bancoId);
      if (b) { b.nome = nome; return b; }
    }
    return null;
  }

  const [linha] = await q(
    supabase.from('empresa_bancos').update({ nome }).eq('id', bancoId).select(CAMPOS_BANCO)
  );
  return linha;
}

/**
 * Baixa lógica, nunca exclusão.
 *
 * A conta some da tela, mas os documentos já marcados continuam ligados a
 * ela. Apagar a linha levaria junto, por cascata, o registro de que o
 * extrato de março chegou — e esse registro é o trabalho de alguém.
 *
 * Isso importa agora porque a varredura das pastas criou contas demais
 * (rótulo de pasta não é nome de conta), e a limpeza vai ser feita pela
 * equipe, conta por conta, enquanto usa.
 */
export async function removerBanco(bancoId) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    for (const e of Demo.empresas) {
      const b = (e.bancos || []).find((x) => x.id === bancoId);
      if (b) { b.ativo = false; return; }
    }
    return;
  }

  await q(supabase.from('empresa_bancos').update({ ativo: false }).eq('id', bancoId));
}

export async function listarColaboradores() {
  if (Demo.emDemonstracao) return Demo.colaboradores;   // guarda do modo demonstração

  return q(
    supabase
      .from('colaboradores')
      .select('id, nome, email, papel, ativo')
      .eq('ativo', true)
      .order('nome')
  );
}

/* ------------------------------------------------------------------ */
/* Situação do cliente                                                 */
/*                                                                     */
/* ativo         — normal                                              */
/* sem_movimento — continua cliente e continua gerando competência,    */
/*                 só aparece sinalizado (ainda tem acessória)         */
/* encerrado     — ex-cliente, não gera mais nada                      */
/* ------------------------------------------------------------------ */

export async function alterarSituacao(id, { situacao, cliente_desde, cliente_ate, motivo_saida }) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const e = Demo.empresas.find((x) => x.id === id);
    if (e) Object.assign(e, { situacao, cliente_desde, cliente_ate, motivo_saida });
    return e;
  }

  const [linha] = await q(
    supabase
      .from('empresas')
      .update({ situacao, cliente_desde, cliente_ate, motivo_saida })
      .eq('id', id)
      .select(CAMPOS)
  );
  return linha;
}

/**
 * Encerrar cliente.
 *
 * Chame com confirmar=false primeiro: nada é apagado e volta quantas
 * competências seriam removidas e quantas já têm trabalho. A tela mostra
 * os números e só então chama com true.
 */
export async function encerrar(id, { data, motivo = null, confirmar = false }) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const corte = data.slice(0, 7) + '-01';
    const alvo = Demo.fechamentos.filter(
      (f) => f.empresa_id === id && f.competencia > corte
    );
    const comTrabalho = alvo.filter((f) =>
      f.status === 'concluido'
      || Demo.marcacoes.some((m) => m.fechamento_id === f.id && m.feito)
      || Demo.itens.some((i) => i.fechamento_id === f.id && i.status !== 'pendente')
    ).length;

    if (confirmar) {
      const ids = new Set(alvo.map((f) => f.id));
      for (let i = Demo.fechamentos.length - 1; i >= 0; i--) {
        if (ids.has(Demo.fechamentos[i].id)) Demo.fechamentos.splice(i, 1);
      }
      for (let i = Demo.itens.length - 1; i >= 0; i--) {
        if (ids.has(Demo.itens[i].fechamento_id)) Demo.itens.splice(i, 1);
      }
      for (let i = Demo.marcacoes.length - 1; i >= 0; i--) {
        if (ids.has(Demo.marcacoes[i].fechamento_id)) Demo.marcacoes.splice(i, 1);
      }
      const e = Demo.empresas.find((x) => x.id === id);
      if (e) Object.assign(e, { situacao: 'encerrado', cliente_ate: data, motivo_saida: motivo });
    }

    return { removidas: alvo.length, com_trabalho: comTrabalho };
  }

  const { data: linhas, error } = await supabase.rpc('encerrar_cliente', {
    p_empresa: id, p_data: data, p_motivo: motivo, p_confirmar: confirmar,
  });
  if (error) throw new Error(error.message);
  return linhas?.[0] ?? { removidas: 0, com_trabalho: 0 };
}

export async function reativar(id) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const e = Demo.empresas.find((x) => x.id === id);
    if (e) Object.assign(e, { situacao: 'ativo', cliente_ate: null, motivo_saida: null });
    return;
  }

  const { error } = await supabase.rpc('reativar_cliente', { p_empresa: id });
  if (error) throw new Error(error.message);
}

/* ------------------------------------------------------------------ */
/* Feito até, por frente                                               */
/*                                                                     */
/* A pessoa preenche o checklist e depois avisa por recado "conciliação */
/* feita até tal mês". É a mesma informação duas vezes, e a segunda vez */
/* não fica registrada. Isto lê direto dos itens de conferência.       */
/* ------------------------------------------------------------------ */

export const FRENTES = ['Docs', 'Folha', 'Fiscal', 'Contábil', 'Conciliação'];

export async function frentesDaEmpresa(empresaId) {
  if (Demo.emDemonstracao) return frentesDemo(empresaId);   // guarda do modo demonstração

  return q(
    supabase
      .from('v_frente_feita_ate')
      .select('frente, feita_ate, primeiro_aberto, ok_fora_de_ordem')
      .eq('empresa_id', empresaId)
  );
}

function frentesDemo(empresaId) {
  const fechs = Demo.fechamentos
    .filter((f) => f.empresa_id === empresaId)
    .sort((a, b) => a.competencia.localeCompare(b.competencia));

  return FRENTES.map((frente) => {
    let feita_ate = null;
    let primeiro_aberto = null;
    let ok_fora_de_ordem = 0;

    for (const f of fechs) {
      const item = Demo.itens.find((i) => i.fechamento_id === f.id && i.nome === frente);
      // Competência sem o item registrado conta como não feita — senão a
      // frente aparece como "não começou" e "nada em aberto" ao mesmo tempo.
      if (item?.status === 'ok') {
        if (primeiro_aberto) ok_fora_de_ordem++;
        else feita_ate = f.competencia;
      } else if (!primeiro_aberto) {
        primeiro_aberto = f.competencia;
      }
    }
    return { frente, feita_ate, primeiro_aberto, ok_fora_de_ordem };
  });
}

/**
 * O mesmo "feito até", de todas as empresas de uma vez.
 *
 * Para a tela não precisar de uma consulta por empresa: são 161 empresas
 * × 5 frentes, uma chamada só.
 */
export async function frentesDeTodas() {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const mapa = new Map();
    for (const e of Demo.empresas) mapa.set(e.id, frentesDemo(e.id));
    return mapa;
  }

  const linhas = await q(
    supabase
      .from('v_frente_feita_ate')
      .select('empresa_id, frente, feita_ate, primeiro_aberto, ok_fora_de_ordem')
  );

  const mapa = new Map();
  for (const l of linhas) {
    if (!mapa.has(l.empresa_id)) mapa.set(l.empresa_id, []);
    mapa.get(l.empresa_id).push(l);
  }
  return mapa;
}
